import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { EditListFacade } from '../../state/edit-list.facade';
import { toSignal } from '@angular/core/rxjs-interop';
import { CoverSelectionDialogComponent } from '../cover-selection-dialog/cover-selection-dialog.component';
import { UpdateListRequest } from '../../services/models/list.request';
import { ConfigService } from '../../../services/config.service';
import { TextInputComponent } from '../../../_shared/components/forms/text-input/text-input.component';
import { LocaleService } from '../../../services/locale.service';
import { TextareaComponent } from '../../../_shared/components/forms/textarea/textarea.component';

@Component({
  selector: 'app-edit-list-dialog',
  templateUrl: './edit-list-dialog.component.html',
  styleUrls: ['./edit-list-dialog.component.scss']
})
export class EditListDialogComponent {
  private readonly configService = inject(ConfigService);
  private readonly editListFacade = inject(EditListFacade);
  private readonly isEditing = computed(() => this.list() && this.list().id !== undefined);

  @ViewChild(CoverSelectionDialogComponent) selectionDialog!: CoverSelectionDialogComponent;
  @ViewChild('listNameInput') listNameInput!: TextInputComponent;
  @ViewChild('descriptionTextarea') descriptionTextarea!: TextareaComponent;

  readonly canCreatePublicList = signal(!this.configService.get().publicListCreationDisabled);
  readonly openPrivateWarningDialog = signal(false);
  readonly descriptionMaxLength = signal(300);
  readonly descriptionError = signal<string | null>(null);
  readonly open = toSignal(this.editListFacade.open$);
  readonly list = toSignal(this.editListFacade.list$);
  readonly record = toSignal(this.editListFacade.record$);
  readonly selectedCoverUrl = computed<string | undefined>(() => {
    if (this.list() && this.list().items?.length > 0) {
      const covers = this.list().items.map((i) => i.coverUrl);

      if (this.list().customCoverUrl) {
        covers.unshift(this.list().customCoverUrl);
      }

      return covers[this.list().selectedCoverIdx];
    }

    if (this.record()) {
      return this.record().coverImageUrl;
    }

    return undefined;
  });
  readonly title = computed(() => {
    return this.isEditing() ? 'button.editList' : 'button.newList';
  });

  closeModal() {
    this.descriptionError.set(null);
    this.editListFacade.closeDialog();
  }

  saveList(event: Event) {
    event.preventDefault();

    if (!this.list()?.name) {
      this.listNameInput.focus();
      this.editListFacade.updateLocalList({ name: '' });
      return;
    }

    if (this.descriptionError() !== null) {
      this.descriptionTextarea.focus();
      return;
    }

    if (!this.isEditing()) {
      this.editListFacade.createList(this.list(), this.record());
    } else {
      const request: UpdateListRequest = {
        ...this.list(),
        selectedCoverIdx: this.calculateSelectedCoverIdx(),
        deleteCustomCover: this.shouldDeleteCustomCover(),
      }

      this.editListFacade.editList(request);
    }
  }

  openCoverSelectionDialog() {
    this.selectionDialog.openDialog();
  }

  updateListName(name: string) {
    this.editListFacade.updateLocalList({ name });
  }

  updateListDescription(description: string) {
    this.editListFacade.updateLocalList({ description });

    if (description.length > this.descriptionMaxLength()) {
      this.descriptionError.set('textarea.charCountExceeded');
    } else {
      this.descriptionError.set(null);
    }
  }

  updateListPrivacy(isPublic: boolean) {
    if (this.isEditing() && !this.list().privateList && !isPublic && this.list().followers > 0) {
      this.openPrivateWarningDialog.set(true);
    }

    this.editListFacade.updateLocalList({ privateList: !isPublic });
  }

  handlePrivacyWarningDialogResponse(isPrivate: boolean) {
    this.openPrivateWarningDialog.set(false);
    this.editListFacade.updateLocalList({ privateList: isPrivate });
  }

  private calculateSelectedCoverIdx(): number {
    if (this.list().customCoverUrl) {
      if (this.list().selectedCoverIdx === 0) {
        return 0;
      }

      return this.list().selectedCoverIdx - 1;
    }

    return this.list().selectedCoverIdx;
  }

  private shouldDeleteCustomCover(): boolean {
    if (this.list().customCoverUrl) {
      return this.list().selectedCoverIdx !== 0;
    }

    return false;
  }
}
